/**
 * A web room tab leaves the call when its account signs out in another tab.
 *
 * Signing out in the portal (client-profile.js, interpreter-profile.html)
 * retires that page and clears the shared localStorage credentials, but a room
 * opened from it is a separate page: it stayed in the call with camera and
 * microphone on. Worse, it keeps its own sessionStorage copy of the
 * credentials, and storage.ts mirrors a session copy back into localStorage
 * on the next read, which could put the signed-out credentials back.
 *
 * The sign-out marker is the shared refresh token disappearing. A token
 * refresh rewrites it and never removes it; the sign-in handoff's rollback
 * (shared/browser-auth-session.js) removes only the access token. The check
 * runs synchronously in the storage event, before this page can read (and so
 * re-mirror) anything.
 */
import { CONFERENCE_JOINED, CONFERENCE_LEFT } from '../base/conference/actionTypes';
import { hangup } from '../base/connection/actions';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { removePersistentItem } from './storage';
/** The browser credential keys (shared/browser-auth-session.js KEYS). */
export const BROWSER_AUTH_KEYS = [
    'vrs_auth_token',
    'vrs_refresh_token',
    'vrs_user_info',
    'vrs_user_role',
    'vrs_client_auth',
    'vrs_interpreter_auth',
    'vrs_interpreter_available',
    'vrs_shift_started_at'
];
function read(storage, key) {
    try {
        return storage?.getItem(key) ?? null;
    }
    catch {
        return null;
    }
}
/** The room was joined with the account's sign-in, not a guest invite alone. */
export function joinedWithAccount(local, session) {
    return read(local, 'vrs_refresh_token') !== null || read(session, 'vrs_refresh_token') !== null;
}
/** This storage change is the account signing out in another tab. */
export function isSignOutElsewhere(event, local) {
    if (!local || event.storageArea !== local) {
        return false;
    }
    if (event.key !== null && (event.key !== 'vrs_refresh_token' || event.newValue !== null)) {
        return false;
    }
    return read(local, 'vrs_refresh_token') === null;
}
function browserStorage(name) {
    try {
        return typeof window === 'undefined' ? undefined : window[name];
    }
    catch {
        return undefined;
    }
}
let stopWatching;
function watch(dispatch) {
    const onStorage = (event) => {
        if (!isSignOutElsewhere(event, browserStorage('localStorage'))) {
            return;
        }
        stopWatching?.();
        // This tab's copies go too, so nothing can mirror them back.
        BROWSER_AUTH_KEYS.forEach(removePersistentItem);
        dispatch(hangup(false));
    };
    window.addEventListener('storage', onStorage);
    stopWatching = () => {
        window.removeEventListener('storage', onStorage);
        stopWatching = undefined;
    };
}
MiddlewareRegistry.register(({ dispatch }) => next => action => {
    const result = next(action);
    if (action.type === CONFERENCE_JOINED && !stopWatching && typeof window !== 'undefined'
        && joinedWithAccount(browserStorage('localStorage'), browserStorage('sessionStorage'))) {
        watch(dispatch);
    }
    else if (action.type === CONFERENCE_LEFT) {
        stopWatching?.();
    }
    return result;
});
